/**
 * Admin → Klantvereisten → Verwerkingen (EU-verwerking sprint 1, 24 okt 2026).
 * Wat ging waarheen: aantallen per functie × aanbieder × regio, plus de controle
 * "buiten EU ná aanzetten vlag" (moet 0 zijn). Alleen lezen; CSV voor de klant/DPA-vragen.
 * Kosten volgen in sprint 2 (tot dan: "onbekend", nooit €0).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson, meldFout } from '../../../utils/apiFout';

const API = `${BACKEND_URL}/Api/klantvereisten/beheer`;
const STIL = '#767c7e';

const iso = (d) => d.toISOString().slice(0, 10);

export function EuRouteStatus({ status }) {
  if (!status) return null;
  return (
    <Typography sx={{ fontSize: '0.85rem', color: STIL }}>
      Azure-route (EU) actief: <b>{status.actief ? 'ja' : 'nee'}</b>
      {!status.actief && ' — zolang dit nee is, wacht of stopt verwerking voor EU-klanten met een melding.'}
    </Typography>
  );
}

export default function KlantVereistenVerwerkingen() {
  const nu = new Date();
  const [van, setVan] = useState(iso(new Date(nu.getFullYear(), nu.getMonth(), 1)));
  const [tot, setTot] = useState(iso(nu));
  const [data, setData] = useState(null);
  const [bezig, setBezig] = useState(false);
  const url = (csv) => `${API}/verwerkingen?van=${van}&tot=${tot}${csv ? '&csv=true' : ''}`;

  const laad = useCallback(async () => {
    setBezig(true);
    try { setData(await leesJson(await authFetch(url(false)), 'Verwerkingen laden')); }
    catch (e) { meldFout(e, 'Verwerkingen laden'); }
    finally { setBezig(false); }
  }, [van, tot]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { laad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      const r = await authFetch(url(true));
      if (!r.ok) await leesJson(r, 'CSV downloaden');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ai-verwerkingen-${van}-${tot}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { meldFout(e, 'CSV downloaden'); }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.85rem', color: STIL, mb: 1.5 }}>
        Elke AI-aanroep wordt vastgelegd (aanbieder, regio, beleid; nooit inhoud). Logging loopt sinds de livegang
        van EU-verwerking; daarvoor zijn er geen gegevens. Kosten volgen later.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
        <TextField size="small" type="date" label="Van" value={van} onChange={(e) => setVan(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Tot en met" value={tot} onChange={(e) => setTot(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" onClick={laad} disabled={bezig} sx={{ textTransform: 'none' }}>Bekijken</Button>
        <Button size="small" onClick={download} sx={{ textTransform: 'none' }}>CSV downloaden</Button>
      </Stack>
      {bezig && !data && <CircularProgress size={20} />}
      {data && (
        <Stack spacing={1.5}>
          <EuRouteStatus status={data.euRoute} />
          {data.euGroepen.length === 0 ? (
            <Alert severity="info" variant="outlined">Geen klantgroep met EU-verwerking aan: alles loopt via de standaardroute.</Alert>
          ) : (
            <Alert severity={data.buitenEuNaVlagAantal ? 'error' : 'success'} variant="outlined">
              Buiten de EU verwerkt ná het aanzetten van de vlag: <b>{data.buitenEuNaVlagAantal}</b>
              {data.buitenEuNaVlagAantal ? ' — dit hoort 0 te zijn. Zie de CSV voor de regels.' : ' (zoals het hoort).'}
              {' '}EU-klanten: {data.euGroepen.map((g) => `${g.titel}${g.euSinds ? ` (sinds ${g.euSinds.slice(0, 10)})` : ''}`).join(', ')}.
            </Alert>
          )}
          {(data.zonderEinde > 0 || data.koppelingOnzeker) && (
            <Alert severity="warning" variant="outlined">
              {data.zonderEinde > 0 && `${data.zonderEinde} aanroep(en) zonder eindregel: uitkomst onbekend. `}
              {data.koppelingOnzeker && 'Niet elke projectkoppeling is te volgen; controleer de CSV.'}
            </Alert>
          )}
          {data.nietToeTeWijzen > 0 && data.euGroepen.length > 0 && (
            <Typography sx={{ fontSize: '0.85rem', color: STIL }}>
              VS-verwerkingen zonder project of meeting (niet toe te wijzen): {data.nietToeTeWijzen}.
            </Typography>
          )}
          {(data.voorVlagAantal > 0 || data.achterafGekoppeldAantal > 0) && (
            <Typography sx={{ fontSize: '0.85rem', color: STIL }}>
              Vóór de vlag in de VS verwerkt: {data.voorVlagAantal}. Achteraf aan een EU-project gekoppeld, eerder VS: {data.achterafGekoppeldAantal}.
            </Typography>
          )}
          {data.rijen.length === 0 ? (
            <Typography sx={{ fontSize: '0.85rem', color: STIL }}>Geen verwerkingen in deze periode.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Functie</TableCell><TableCell>Aanbieder</TableCell><TableCell>Regio</TableCell>
                  <TableCell>Beleid</TableCell><TableCell>Status</TableCell><TableCell align="right">Aantal</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.rijen.map((r) => (
                    <TableRow key={`${r.functie}|${r.aanbieder}|${r.regio}|${r.beleid}|${r.status}`}>
                      <TableCell>{r.functie}</TableCell><TableCell>{r.aanbieder}</TableCell>
                      <TableCell>{r.regio === 'eu' ? 'EU' : 'VS'}</TableCell><TableCell>{r.beleid}</TableCell>
                      <TableCell>{r.status}</TableCell><TableCell align="right">{r.aantal}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
